import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/motion";
import { PHONE, PHONE_HREF } from "../lib/site";

const NEXT = [
  { t: "We review your request", d: "Our team reads through your facility details, services and preferred schedule. Nothing is priced automatically." },
  { t: "Walkthrough or consultation", d: "We contact you to arrange an on-site walkthrough or a phone or video consultation, whichever you chose." },
  { t: "Your customized proposal", d: "You receive a written proposal with the cleaning scope by area, visit frequency, service windows and pricing." },
  { t: "Approve and begin", d: "Once you approve, your recurring schedule is set up and service begins. Nothing is booked or charged before then." },
];

const PREP = ["Building access, alarm or after-hours entry details", "Number of restrooms, breakrooms and floors", "Floor types (carpet, tile, LVT, concrete, stone)", "Areas that need special care or should be excluded", "Your current cleaning schedule and what you'd like improved"];

const PROPOSAL = ["Area-by-area cleaning scope", "Recurring visit frequency and days", "Service window that fits your hours", "Periodic tasks planned into the calendar", "Clear pricing for the agreed scope"];

const List = ({ title, items, testid }) => (
  <Reveal className="rounded-[28px] border border-slate-200 bg-white p-8 md:p-10" data-testid={testid}>
    <h2 className="font-display text-2xl font-semibold text-ink md:text-3xl">{title}</h2>
    <ul className="mt-6 space-y-3">
      {items.map((x) => (
        <li key={x} className="flex items-start gap-3 text-base text-slate-700"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />{x}</li>
      ))}
    </ul>
  </Reveal>
);

export default function RequestReceived() {
  useEffect(() => {
    if (window.self !== window.top) window.top.location.href = window.location.href;
  }, []);

  return (
    <>
      <Seo title="Request Received | Shine Solutions" description="Thank you for your commercial cleaning quote request. Here is what happens next." path="/request-received" />
      <Helmet><meta name="robots" content="noindex, follow" /></Helmet>
      <PageHero kicker="Request received" lines={["Thank you.", <>We're on <span className="font-serif font-normal italic text-glint">it.</span></>]} intro="Your commercial cleaning request has been received. Here's exactly what happens next — from a walkthrough of your facility to a written proposal built around it.">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.9, type: "spring", stiffness: 160, damping: 14 }} className="mt-10 inline-flex items-center gap-3 rounded-full border border-glint/40 bg-glint/10 px-5 py-3 text-sm text-white" data-testid="request-received-badge">
          <CheckCircle2 className="h-5 w-5 text-glint" /> Request submitted successfully
        </motion.div>
      </PageHero>

      <section className="bg-white py-24 md:py-32" data-testid="request-received-steps">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><Eyebrow>What happens next</Eyebrow></Reveal>
          <ol className="mt-12 grid gap-px overflow-hidden rounded-[28px] border border-slate-200 bg-slate-200 md:grid-cols-2 lg:grid-cols-4">
            {NEXT.map((s, i) => (
              <Reveal as="li" key={s.t} delay={0.06 * i} className="bg-white p-8">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink font-mono text-xs text-glint">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-8 font-display text-xl font-semibold text-ink">{s.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{s.d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-porcelain py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-6 px-5 md:px-10 lg:grid-cols-2">
          <List title="Helpful to have ready for the walkthrough" items={PREP} testid="request-received-prep" />
          <List title="What your proposal will include" items={PROPOSAL} testid="request-received-proposal" />
        </div>
      </section>

      <section className="bg-ink py-20 text-white md:py-28">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-8 px-5 md:flex-row md:items-center md:justify-between md:px-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-glint">Need to add something?</p>
            <h2 className="mt-4 font-display text-3xl font-semibold md:text-5xl">Call us any time.</h2>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row">
            <a href={PHONE_HREF} data-testid="request-received-call-btn" className="btn-glint !px-7 !py-4"><Phone className="h-4 w-4" /> {PHONE}</a>
            <Link to="/services" data-testid="request-received-services-btn" className="btn-outline-light group">Explore Our Services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
